import { Section } from '@factorialco/deprecated-design-system'
import {
  ContactFragment,
  ProjectDetailsPageSettingsPageBasicInformationSectionFragment,
  ProjectManagementProjectDetailsPageSettingsPageDocument,
  ProjectManagementProjectDetailsPageSettingsPageQuery,
  UpdateProjectDocument,
  UpdateProjectMutation,
} from 'generated/resources/graphql'
import moment from 'moment'

import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateUIToBackend } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  ProjectBasicDetailsForm,
  ProjectBasicDetailsValues,
} from 'modules/project_management/components/forms/ProjectBasicDetailsForm'
import { projectAttributes } from 'modules/project_management/lib/constants'

export const BasicInformationSection: React.FC<{
  project: ProjectDetailsPageSettingsPageBasicInformationSectionFragment
  companyId: number
  hasClientsFeature: boolean
  hasSubprojectsFeature: boolean
  data: ProjectManagementProjectDetailsPageSettingsPageQuery
}> = ({ project, companyId, hasClientsFeature, hasSubprojectsFeature, data }) => {
  const [updateProject, { loading: runningQuery }] = useMutation(UpdateProjectDocument, {
    extractorFn: (data: UpdateProjectMutation) =>
      data.projectManagementMutations.updateProjectmanagementProject,
  })

  const {
    code: projectCode,
    name: projectName,
    startDate,
    dueDate,
    client,
    description: projectDescription,
    legalEntity: projectLegalEntity,
  } = project

  const startDateFormatted = moment(startDate).format('L')
  const dueDateFormatted = moment(dueDate).format('L')

  const { values, setValues, errors, handleSubmit, isValid, dirty } =
    useForm<ProjectBasicDetailsValues>({
      defaultValues: {
        name: projectName || '',
        code: projectCode || undefined,
        description: projectDescription || undefined,
        startDate: startDate ? startDateFormatted : undefined,
        dueDate: dueDate ? dueDateFormatted : undefined,
        client: client || null,
        legalEntityId: projectLegalEntity?.id?.toString() || '',
      },
      enableReinitialize: true,
      validate: (data) => {
        const errorValidation: FormErrors<ProjectBasicDetailsValues> = {}

        if (!data.name) {
          errorValidation.name = i18n.t('project_management.modals.error_messages.mandatory')
        }
        return errorValidation
      },
      onSubmit: async (data, { setErrors }) => {
        const result = await updateProject({
          variables: {
            id: project.id,
            name: data.name,
            code: data.code,
            description: data.description,
            startDate: data.startDate ? dateUIToBackend(data.startDate) : undefined,
            dueDate: data.dueDate ? dateUIToBackend(data.dueDate) : undefined,
            clientId: data.client ? data.client.id : undefined,
            legalEntityId: data.legalEntityId ? parseInt(data.legalEntityId, 10) : undefined,
          },
          awaitRefetchQueries: true,
          refetchQueries: [
            {
              query: ProjectManagementProjectDetailsPageSettingsPageDocument,
              variables: { projectId: project.id, companyId },
            },
          ],
        })

        const onSubmitError = () => {
          setErrors(errors)
        }

        const handled = handleResult(result, (err) => {
          if (err.name)
            errors.name = i18n.t('project_management.modals.error_messages.project_name_exists')
          if (err.code)
            errors.code = i18n.t('project_management.modals.error_messages.project_code_exists')
          onSubmitError()
        })

        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('common.saved_successfully'),
          })
        }

        track(trackingEvents.projectManagement.settings.updateProject)

        return handled
      },
    })

  const handleFieldChanged = (
    fieldName: string,
    fieldValue: string | undefined | boolean | ContactFragment
  ) => {
    if (fieldName === projectAttributes.projectCode && fieldValue === '') {
      fieldValue = undefined
    }
    setValues({ ...values, [fieldName]: fieldValue })
  }

  return (
    <Section.Form
      illustration="settings"
      title={i18n.t('project_management.settings.sections.basic_information.title')}
      description={i18n.t('project_management.settings.sections.basic_information.description')}
      onSubmit={handleSubmit}
      submitLabel={i18n.t('common.submit')}
      disabled={runningQuery || !isValid}
      isDirty={dirty}
      paddingX="s0"
    >
      <ProjectBasicDetailsForm
        values={values}
        errors={errors}
        onChangeValue={handleFieldChanged}
        companyId={companyId}
        hasClientsFeature={hasClientsFeature}
        data={data}
        hasSubprojectsFeature={hasSubprojectsFeature}
      />
    </Section.Form>
  )
}
